/**
 * CompletionScreen
 *
 * Shown after conversation transcript is successfully submitted.
 * Displays role-specific message, next steps.
 * NO scores unless trainer enabled showScoreToLearner.
 */

interface ScoreResult {
  overallScore: number;
  normalizedScore: number;
  passed: boolean;
}

interface CompletionScreenProps {
  submissionId: string | null;
  showScoreToLearner: boolean;
  scoreResult: ScoreResult | null;
  scoringFailed: boolean;
  /** Persona role name from assessment metadata (e.g., "Sales Associate") */
  roleName?: string;
}

export function CompletionScreen({
  submissionId,
  showScoreToLearner,
  scoreResult,
  scoringFailed,
  roleName,
}: CompletionScreenProps) {
  return (
    <div className="learner-completion">
      <div className="learner-completion-card">
        {/* Success icon */}
        <div className="learner-completion-icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="32" fill="var(--emerald-100)" />
            <path
              d="M20 32L28 40L44 24"
              stroke="var(--emerald-600)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h2 className="learner-completion-title">Assessment Complete</h2>

        <p className="learner-completion-message">
          {roleName
            ? `Your ${roleName} assessment conversation has been recorded and submitted for evaluation.`
            : 'Your conversation has been recorded and submitted for evaluation.'}
        </p>

        {/* Score display (only if trainer enabled it) */}
        {showScoreToLearner && scoreResult && (
          <div className="learner-completion-score">
            <div className={`learner-score ${scoreResult.passed ? 'passed' : 'not-passed'}`}>
              <span className="score-label">Your Score</span>
              <span className="score-value">
                {Math.round(scoreResult.normalizedScore * 100)}%
              </span>
            </div>
            <div className={`score-status ${scoreResult.passed ? 'passed' : 'not-passed'}`}>
              {scoreResult.passed ? 'Passed' : 'Did not pass'}
            </div>
          </div>
        )}

        {/* Scoring pending message */}
        {showScoreToLearner && scoringFailed && !scoreResult && (
          <div className="warning-box learner-completion-warning">
            <p>Score calculation is in progress. Check back later or contact your trainer.</p>
          </div>
        )}

        {/* Next steps */}
        <div className="learner-completion-next">
          <p>Your trainer will review the results. You may close this page.</p>
        </div>

        {/* Reference ID */}
        {submissionId && (
          <div className="learner-completion-ref">
            <span className="text-small text-muted">
              Reference: <span className="mono">{submissionId}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
